/**
 * The Golden Book · /goldenbook — Le Livre d'Or de la Maison.
 * Founder-approved 19 Jul 2026. The house's private register, kept by
 * four concierges — 28 years each, 112 in the room. Partner clubs ride
 * on MindEscapes Membership; verified entries carry the Keeper's seal.
 * Embed contract: ?embed=widget&tenant=aurelion&clubs=1&member_mc_no=…
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: "#F2EBDD", card: "#FBF7EE", ink: "#1F1A12", ink2: "#5C5447",
  ink3: "#8A8071", line: "#E2D8C3", line2: "#D3C6AA",
  brass: "#8B6914", brassSoft: "#B69152", seal: "#6E5210",
};

const KEEPERS = [
  { key: "dipali", name: "Dipali Sikand", mono: "D", img: "/img/keepers/dipali.png",
    email: "dipali@clubconcierge.in",
    domain: "The Tables", line: "Founder of the house, 1998. Where she books, the kitchen knows." },
  { key: "nisha", name: "Nisha Verma", mono: "N", img: "/img/keepers/nisha.png",
    email: "nisha@clubconcierge.in",
    domain: "The Rooms", line: "Keeper of the clubs — near four thousand doors answer her call." },
  { key: "samar", name: "Samar Rathore", mono: "S", img: "/img/keepers/samar.png",
    email: "samar@clubconcierge.in",
    domain: "The Evenings", line: "Keeper of the night — the terrace, the table after nine." },
  { key: "srikanth", name: "Srikanth DS", mono: "S", img: "/img/keepers/srikanth.png",
    email: "srikanth.ds@lesconcierges.co.in",
    domain: "The Sanctuaries", line: "Keeper of stillness — where the house sends you to breathe." },
];

const GROUPS = [
  { key: "tables", label: "The Tables", keeper: "dipali" },
  { key: "evenings", label: "The Evenings", keeper: "samar" },
  { key: "sanctuaries", label: "The Sanctuaries", keeper: "srikanth" },
  { key: "rooms", label: "The Rooms", keeper: "nisha" },
];

const FEATURED = ["Mumbai", "Delhi", "Bengaluru", "Hyderabad", "Goa", "Dubai",
                  "Singapore", "London", "New York", "Paris", "Bangkok", "Tokyo"];

function urlParams() {
  try { return new URL(window.location.href).searchParams; } catch { return new URLSearchParams(); }
}

const keeperOf = (key) => KEEPERS.find((k) => k.key === key) || KEEPERS[0];

export default function GoldenBookPage() {
  const p = useMemo(urlParams, []);
  const isEmbed = p.get("embed") === "widget";
  const clubsEntitled = !isEmbed || p.get("clubs") === "1";
  const mcNo = p.get("member_mc_no") || "";
  const em = useEmbedMember();

  const [overview, setOverview] = useState(null);
  const [city, setCity] = useState("Mumbai");
  const [group, setGroup] = useState("tables");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arranging, setArranging] = useState(null); // entry
  const [upsell, setUpsell] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/goldenbook/overview`).then((r) => r.json())
      .then(setOverview).catch(() => {});
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    fetch(`${API}/api/goldenbook/register?city=${encodeURIComponent(city)}&group=${group}&clubs=${clubsEntitled ? 1 : 0}`)
      .then((r) => r.json())
      .then((j) => { if (live) { setEntries(j.entries || []); setLoading(false); } })
      .catch(() => { if (live) { setEntries([]); setLoading(false); } });
    return () => { live = false; };
  }, [city, group, clubsEntitled]);

  const cities = useMemo(() => {
    const fromApi = (overview?.cities || []).map((c) => c.city);
    const ordered = FEATURED.filter((c) => fromApi.includes(c));
    const rest = fromApi.filter((c) => !FEATURED.includes(c)).sort();
    return [...ordered, ...rest];
  }, [overview]);

  return (
    <div data-testid="goldenbook-page"
         style={{ background: T.bg, minHeight: "100vh", color: T.ink,
                  fontFamily: "'Fraunces', Georgia, serif", paddingBottom: 90 }}>
      {!isEmbed && (
        <header data-testid="goldenbook-header"
                style={{ borderBottom: `1px solid ${T.line}`, background: T.bg,
                         position: "sticky", top: 0, zIndex: 20 }}>
          <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex",
                        alignItems: "center", justifyContent: "space-between",
                        padding: "14px 22px", gap: 10, minWidth: 0 }}>
            <Link to="/" data-testid="goldenbook-home-button"
                  style={{ textDecoration: "none", color: T.ink, display: "inline-flex",
                           alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 22, fontWeight: 500 }}>The Golden Book</span>
              <span className="lc-mobile-hide-on-narrow"
                    style={{ fontFamily: "ui-monospace, monospace", fontSize: 11,
                             letterSpacing: "0.22em", textTransform: "uppercase",
                             color: T.ink3, whiteSpace: "nowrap" }}>
                by Concierge®
              </span>
            </Link>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <CrossSurfaceNav testidPrefix="goldenbook-header-nav" size="compact" />
              <ConciergeSurfaceMenu testid="goldenbook-surface-menu" />
            </div>
          </div>
        </header>
      )}

      {/* ── Hero ── */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "52px 32px 8px" }}>
        <p style={{ letterSpacing: "0.18em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px", fontWeight: 700,
                    fontFamily: "'Inter', sans-serif" }}>
          Le Livre d'Or de la Maison
        </p>
        <h1 style={{ fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.06,
                     fontWeight: 500, margin: "0 0 14px" }}>
          The Golden Book<span style={{ color: T.brass }}>.</span>
        </h1>
        <p style={{ color: T.ink2, fontSize: 17, lineHeight: 1.6, maxWidth: 640,
                    margin: 0, fontFamily: "'Inter', sans-serif" }}>
          Google tells everyone the same thing. This register holds what
          112 years in the room knows — every entry inscribed, sealed, and
          defended by one of four hands.
        </p>
      </section>

      {/* ── The Four Keepers ── */}
      <section data-testid="goldenbook-keepers"
               style={{ maxWidth: 1080, margin: "0 auto", padding: "36px 32px 8px" }}>
        <div style={{ display: "grid", gap: 22,
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {KEEPERS.map((k) => (
            <div key={k.key} data-testid={`goldenbook-keeper-${k.key}`}
                 style={{ background: T.card, border: `1px solid ${T.line}`,
                          borderRadius: 4, overflow: "hidden", position: "relative" }}>
              <img src={k.img} alt={k.name}
                   style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover",
                            display: "block", filter: "sepia(0.12)" }} />
              <div style={{ position: "absolute", top: 12, right: 12, width: 34,
                            height: 34, borderRadius: 999, background: T.seal,
                            color: "#F6EAC9", display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 16, fontWeight: 600,
                            boxShadow: "0 2px 8px rgba(31,26,18,0.35)",
                            border: "1px solid rgba(246,234,201,0.5)" }}>
                {k.mono}
              </div>
              <div style={{ padding: "14px 16px 16px" }}>
                <div style={{ fontSize: 16.5, fontWeight: 600 }}>{k.name}</div>
                <div style={{ fontSize: 11, letterSpacing: "0.16em", color: T.brass,
                              textTransform: "uppercase", fontWeight: 700,
                              margin: "4px 0 8px", fontFamily: "'Inter', sans-serif",
                              display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{k.domain} · 28 years</span>
                  <a href={`mailto:${k.email}`}
                     data-testid={`goldenbook-keeper-email-${k.key}`}
                     title={`Write to ${k.name.split(" ")[0]} — ${k.email}`}
                     aria-label={`Email ${k.name}`}
                     style={{ color: T.brass, display: "inline-flex",
                              alignItems: "center", lineHeight: 0 }}>
                    <Mail size={14} strokeWidth={1.8} />
                  </a>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, color: T.ink2,
                              fontStyle: "italic" }}>
                  {k.line}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ textAlign: "center", margin: "22px 0 0", fontSize: 13.5,
                    color: T.ink3, letterSpacing: "0.14em", textTransform: "uppercase",
                    fontFamily: "'Inter', sans-serif" }}>
          28 + 28 + 28 + 28 —{" "}
          <span style={{ color: T.brass, fontWeight: 700 }}>112 years in the room</span>
        </p>
      </section>

      {/* ── Leaf through the Book ── */}
      <FlipBook />

      {/* ── City + group selection ── */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "36px 32px 0" }}>
        <div data-testid="goldenbook-city-chips"
             style={{ display: "flex", flexWrap: "wrap", gap: 8,
                      fontFamily: "'Inter', sans-serif" }}>
          {cities.slice(0, 18).map((c) => (
            <button key={c} type="button"
                    data-testid={`goldenbook-city-${c.replace(/\W/g, "")}`}
                    onClick={() => setCity(c)}
                    style={{ background: city === c ? T.ink : "transparent",
                             color: city === c ? T.bg : T.ink2,
                             border: `1px solid ${city === c ? T.ink : T.line2}`,
                             borderRadius: 999, padding: "7px 15px", fontSize: 12.5,
                             cursor: "pointer", transition: "background-color .15s" }}>
              {c}
            </button>
          ))}
          {cities.length > 18 && (
            <select value={cities.slice(0, 18).includes(city) ? "" : city}
                    data-testid="goldenbook-city-more"
                    onChange={(e) => e.target.value && setCity(e.target.value)}
                    style={{ borderRadius: 999, border: `1px solid ${T.line2}`,
                             background: "transparent", color: T.ink2,
                             padding: "7px 12px", fontSize: 12.5 }}>
              <option value="">more cities…</option>
              {cities.slice(18).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
        <div data-testid="goldenbook-group-tabs"
             style={{ display: "flex", gap: 26, marginTop: 30, flexWrap: "wrap",
                      borderBottom: `1px solid ${T.line}` }}>
          {GROUPS.map((g) => (
            <button key={g.key} type="button"
                    data-testid={`goldenbook-group-${g.key}`}
                    onClick={() => setGroup(g.key)}
                    style={{ background: "transparent", border: 0, cursor: "pointer",
                             padding: "8px 2px 14px", fontSize: 17,
                             fontFamily: "'Fraunces', Georgia, serif",
                             color: group === g.key ? T.ink : T.ink3,
                             fontWeight: group === g.key ? 600 : 400,
                             borderBottom: group === g.key
                               ? `2px solid ${T.brass}` : "2px solid transparent" }}>
              {g.label}
            </button>
          ))}
        </div>
        <p style={{ margin: "14px 0 0", fontSize: 12.5, color: T.ink3,
                    fontFamily: "'Inter', sans-serif" }}>
          Kept by <strong style={{ color: T.brass }}>
            {keeperOf(GROUPS.find((g) => g.key === group)?.keeper).name}
          </strong> · {city}
        </p>
      </section>

      {/* ── The register ── */}
      <section style={{ maxWidth: 1080, margin: "0 auto", padding: "22px 32px 0" }}>
        {loading ? (
          <p data-testid="goldenbook-loading"
             style={{ color: T.ink3, fontStyle: "italic", padding: "30px 0" }}>
            Opening the Book…
          </p>
        ) : entries.length === 0 && !(group === "rooms" && !clubsEntitled) ? (
          <p data-testid="goldenbook-empty"
             style={{ color: T.ink3, fontStyle: "italic", padding: "30px 0" }}>
            No entries inscribed for {city} here yet — the Keepers are still writing.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 20,
                        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
            {group === "rooms" && !clubsEntitled && (
              <div data-testid="goldenbook-upsell-tile"
                   style={{ gridColumn: "1 / -1", background: T.ink, color: T.bg,
                            borderRadius: 6, padding: "26px 28px", display: "flex",
                            flexWrap: "wrap", alignItems: "center", gap: 18,
                            justifyContent: "space-between" }}>
                <div style={{ maxWidth: 560 }}>
                  <div style={{ fontSize: 19, fontWeight: 600, marginBottom: 6 }}>
                    Clubs of the World
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85,
                                fontFamily: "'Inter', sans-serif" }}>
                    3,700+ private members' clubs — golf, city & social, yacht —
                    ride on MindEscapes Membership. Your programme doesn't include
                    it yet; the house can arrange it.
                  </div>
                </div>
                <button type="button" data-testid="goldenbook-upsell-button"
                        onClick={() => setUpsell(true)}
                        style={{ background: T.brassSoft, color: T.ink, border: 0,
                                 borderRadius: 999, padding: "13px 26px", fontSize: 13,
                                 fontWeight: 700, letterSpacing: "0.08em",
                                 textTransform: "uppercase", cursor: "pointer",
                                 fontFamily: "'Inter', sans-serif" }}>
                  Arrange my membership
                </button>
              </div>
            )}
            {entries.map((e) => (
              <EntryCard key={e.entry_id} entry={e}
                         onArrange={() => setArranging(e)} />
            ))}
          </div>
        )}
      </section>

      {arranging && (
        <ArrangeSheet entry={arranging} em={em} mcNo={mcNo}
                      onClose={() => setArranging(null)}
                      onDone={(ref) => {
                        setArranging(null);
                        setToast(`Inscribed. The house is on it — ref ${String(ref).slice(0, 8)}`);
                        setTimeout(() => setToast(null), 6000);
                      }} />
      )}
      {upsell && (
        <UpsellSheet em={em} onClose={() => setUpsell(false)}
                     onDone={() => {
                       setUpsell(false);
                       setToast("Noted — the house will call about your membership.");
                       setTimeout(() => setToast(null), 6000);
                     }} />
      )}
      {toast && (
        <div data-testid="goldenbook-toast"
             style={{ position: "fixed", bottom: 26, left: "50%",
                      transform: "translateX(-50%)", background: T.ink, color: T.bg,
                      borderRadius: 999, padding: "13px 26px", fontSize: 13.5,
                      zIndex: 90, fontFamily: "'Inter', sans-serif",
                      boxShadow: "0 12px 32px rgba(31,26,18,0.35)", maxWidth: "90vw" }}>
          {toast}
        </div>
      )}
    </div>
  );
}

function FlipBook() {
  const [pages, setPages] = useState([]);
  const [idx, setIdx] = useState(0);
  const [turning, setTurning] = useState(null); // "next" | "prev"

  useEffect(() => {
    fetch(`${API}/api/goldenbook/sampling`).then((r) => r.json())
      .then((j) => setPages(j.pages || [])).catch(() => {});
  }, []);

  if (pages.length < 2) return null;
  const turn = (dir) => {
    if (turning) return;
    setTurning(dir);
    setTimeout(() => {
      setIdx((i) => dir === "next"
        ? Math.min(i + 1, pages.length - 1) : Math.max(i - 1, 0));
      setTurning(null);
    }, 380);
  };
  const e = pages[idx];
  const k = keeperOf(e.keeper);

  return (
    <section data-testid="goldenbook-flipbook"
             style={{ maxWidth: 1080, margin: "0 auto", padding: "46px 32px 6px" }}>
      <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                  textTransform: "uppercase", margin: "0 0 16px", fontWeight: 700,
                  fontFamily: "'Inter', sans-serif" }}>
        Leaf through the Book — a sampling
      </p>
      <div style={{ perspective: 1600, display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", width: "min(860px, 100%)", minHeight: 320,
                      boxShadow: "0 18px 48px rgba(31,26,18,0.22)", borderRadius: 6,
                      overflow: "hidden", background: T.card,
                      border: `1px solid ${T.line2}` }}>
          {/* Left leaf · the entry image / plate */}
          <div style={{ flex: 1, minWidth: 0, position: "relative",
                        borderRight: `1px solid ${T.line2}`,
                        background: "linear-gradient(105deg, #EFE7D5 0%, #F7F1E4 60%, #E9DFC9 100%)",
                        transformOrigin: "right center",
                        transition: "transform .38s ease, opacity .38s ease",
                        transform: turning === "prev" ? "rotateY(28deg)" : "rotateY(0deg)",
                        opacity: turning === "prev" ? 0.35 : 1 }}>
            {e.image ? (
              <img src={e.image} alt={e.name}
                   onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                   style={{ position: "absolute", inset: 18, width: "calc(100% - 36px)",
                            height: "calc(100% - 36px)", objectFit: "cover",
                            borderRadius: 4, filter: "sepia(0.18)" }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 84, color: T.brassSoft, opacity: 0.35,
                            fontFamily: "'Fraunces', Georgia, serif" }}>
                {k.mono}
              </div>
            )}
          </div>
          {/* Right leaf · the inscription */}
          <div style={{ flex: 1, minWidth: 0, padding: "30px 32px",
                        display: "flex", flexDirection: "column", gap: 12,
                        transformOrigin: "left center",
                        transition: "transform .38s ease, opacity .38s ease",
                        transform: turning === "next" ? "rotateY(-28deg)" : "rotateY(0deg)",
                        opacity: turning === "next" ? 0.35 : 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.18em", color: T.brass,
                          textTransform: "uppercase", fontWeight: 700,
                          fontFamily: "'Inter', sans-serif" }}>
              {e.city}{e.rating ? ` · ★${e.rating}` : ""}
              {e.tier === "partner" ? " · By MindEscapes Membership" : ""}
            </div>
            <div style={{ fontSize: "clamp(20px, 2.6vw, 28px)", fontWeight: 600,
                          lineHeight: 1.2 }}>
              {e.name}
            </div>
            {e.keeper_note && (
              <div style={{ fontSize: 15, lineHeight: 1.65, color: T.ink2,
                            fontStyle: "italic" }}>
                “{e.keeper_note}”
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: "auto" }}>
              <span style={{ width: 30, height: 30, borderRadius: 999,
                             background: T.seal, color: "#F6EAC9", display: "flex",
                             alignItems: "center", justifyContent: "center",
                             fontSize: 13, fontWeight: 600 }}>
                {k.mono}
              </span>
              <span style={{ fontSize: 12.5, color: T.ink3,
                             fontFamily: "'Inter', sans-serif" }}>
                Inscribed by {k.name}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 18,
                    alignItems: "center", marginTop: 18,
                    fontFamily: "'Inter', sans-serif" }}>
        <button type="button" data-testid="goldenbook-flip-prev"
                disabled={idx === 0} onClick={() => turn("prev")}
                style={{ background: "transparent", border: `1px solid ${T.line2}`,
                         borderRadius: 999, padding: "8px 18px", fontSize: 13,
                         cursor: idx === 0 ? "not-allowed" : "pointer",
                         opacity: idx === 0 ? 0.4 : 1, color: T.ink2 }}>
          ‹ Turn back
        </button>
        <span style={{ fontSize: 12, color: T.ink3, letterSpacing: "0.12em" }}>
          leaf {idx + 1} of {pages.length}
        </span>
        <button type="button" data-testid="goldenbook-flip-next"
                disabled={idx === pages.length - 1} onClick={() => turn("next")}
                style={{ background: T.ink, border: 0, color: T.bg,
                         borderRadius: 999, padding: "8px 18px", fontSize: 13,
                         cursor: idx === pages.length - 1 ? "not-allowed" : "pointer",
                         opacity: idx === pages.length - 1 ? 0.4 : 1 }}>
          Turn the leaf ›
        </button>
      </div>
    </section>
  );
}

function EntryCard({ entry, onArrange }) {
  const k = keeperOf(entry.keeper);
  return (
    <div data-testid={`goldenbook-entry-${entry.entry_id}`}
         style={{ background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: 6, overflow: "hidden", display: "flex",
                  flexDirection: "column" }}>
      {entry.image && (
        <img src={entry.image} alt={entry.name} loading="lazy"
             onError={(e) => { e.currentTarget.style.display = "none"; }}
             style={{ width: "100%", height: 150, objectFit: "cover", display: "block" }} />
      )}
      <div style={{ padding: "16px 18px 18px", display: "flex",
                    flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start",
                      justifyContent: "space-between" }}>
          <div style={{ fontSize: 16.5, fontWeight: 600, lineHeight: 1.3 }}>
            {entry.name}
          </div>
          <span title={`Kept by ${k.name}`}
                style={{ width: 26, height: 26, borderRadius: 999, flexShrink: 0,
                         background: T.seal, color: "#F6EAC9", display: "flex",
                         alignItems: "center", justifyContent: "center",
                         fontSize: 12, fontWeight: 600,
                         fontFamily: "'Fraunces', Georgia, serif" }}>
            {k.mono}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: T.ink3, fontFamily: "'Inter', sans-serif" }}>
          {[entry.subcategory, entry.area].filter(Boolean).join(" · ") || "\u00A0"}
          {entry.rating ? ` · ★${entry.rating}` : ""}
        </div>
        {(entry.keeper_note || entry.note_line) && (
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: T.ink2,
                        fontStyle: "italic", borderLeft: `2px solid ${T.brassSoft}`,
                        paddingLeft: 10 }}>
            “{entry.keeper_note || entry.note_line}”
            {entry.keeper_note && (
              <span style={{ fontStyle: "normal", fontSize: 11, color: T.ink3 }}>
                {" "}— {k.name.split(" ")[0]}
              </span>
            )}
          </div>
        )}
        {entry.tier === "partner" && (
          <div data-testid={`goldenbook-partner-mark-${entry.entry_id}`}
               style={{ fontSize: 10.5, letterSpacing: "0.14em", color: T.brass,
                        textTransform: "uppercase", fontWeight: 700,
                        fontFamily: "'Inter', sans-serif" }}>
            By MindEscapes Membership
          </div>
        )}
        <div style={{ marginTop: "auto", paddingTop: 8 }}>
          <button type="button"
                  data-testid={`goldenbook-arrange-${entry.entry_id}`}
                  onClick={onArrange}
                  style={{ width: "100%", background: T.ink, color: T.bg, border: 0,
                           borderRadius: 999, padding: "12px 0", fontSize: 12.5,
                           fontWeight: 700, letterSpacing: "0.08em",
                           textTransform: "uppercase", cursor: "pointer",
                           fontFamily: "'Inter', sans-serif",
                           touchAction: "manipulation" }}>
            Concierge® arrange it
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "13px 15px", fontSize: 14.5, borderRadius: 10,
  border: `1px solid ${T.line2}`, background: T.bg, color: T.ink,
  fontFamily: "'Inter', sans-serif", outline: "none", boxSizing: "border-box",
};

function SheetShell({ title, sub, children, onClose, testid }) {
  return (
    <div data-testid={testid}
         style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex",
                  alignItems: "flex-end", justifyContent: "center",
                  background: "rgba(31,26,18,0.45)" }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: T.card, borderRadius: "18px 18px 0 0",
                    width: "min(560px, 100vw)", maxHeight: "88vh", overflowY: "auto",
                    padding: "26px 26px 34px",
                    fontFamily: "'Fraunces', Georgia, serif" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{title}</div>
            {sub && <div style={{ fontSize: 13, color: T.ink3, marginTop: 4,
                                  fontFamily: "'Inter', sans-serif" }}>{sub}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
                  data-testid={`${testid}-close`}
                  style={{ background: "transparent", border: 0, fontSize: 22,
                           cursor: "pointer", color: T.ink3, lineHeight: 1 }}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ArrangeSheet({ entry, em, mcNo, onClose, onDone }) {
  const isClub = entry.tier === "partner";
  const [name, setName] = useState(em?.name || "");
  const [phone, setPhone] = useState(em?.phone || "");
  const [email, setEmail] = useState(em?.email || "");
  const [note, setNote] = useState("");
  const [mStatus, setMStatus] = useState(mcNo ? "has_number" : "");
  const [mNumber, setMNumber] = useState(mcNo);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const ready = name.trim() && phone.trim().length >= 5
    && (!isClub || mcNo || (mStatus && (mStatus !== "has_number" || mNumber.trim())));

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${API}/api/goldenbook/arrange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entry_id: entry.entry_id, name: name.trim(), phone: phone.trim(),
          email: email.trim() || null, note: note.trim() || null,
          mc_membership_status: isClub ? (mStatus || null) : null,
          mc_membership_number: isClub && mNumber.trim() ? mNumber.trim() : null,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "failed");
      onDone(j.ref);
    } catch (_) {
      setErr("The house couldn't take that just now — please try again.");
      setBusy(false);
    }
  };

  return (
    <SheetShell testid="goldenbook-arrange-sheet" onClose={onClose}
                title={entry.name}
                sub={isClub ? "By MindEscapes Membership · the desk verifies before booking"
                            : `From the Book · kept by ${keeperOf(entry.keeper).name}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <input data-testid="goldenbook-arrange-name" style={inputStyle}
               placeholder="Your name" value={name} maxLength={120}
               onChange={(e) => setName(e.target.value)} />
        <input data-testid="goldenbook-arrange-phone" style={inputStyle}
               placeholder="WhatsApp number" value={phone} maxLength={40}
               onChange={(e) => setPhone(e.target.value)} />
        <input data-testid="goldenbook-arrange-email" style={inputStyle}
               placeholder="Email (optional)" value={email} maxLength={120}
               onChange={(e) => setEmail(e.target.value)} />
        {isClub && !mcNo && (
          <div data-testid="goldenbook-membership-doors"
               style={{ border: `1px solid ${T.line}`, borderRadius: 12,
                        padding: "14px 16px", fontFamily: "'Inter', sans-serif" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em", color: T.brass,
                          textTransform: "uppercase", fontWeight: 700,
                          marginBottom: 10 }}>
              MindEscapes Membership
            </div>
            {[["has_number", "I have my membership number"],
              ["included_in_programme", "My programme includes it"],
              ["needs_membership", "I don't have one — arrange it for me"]]
              .map(([v, label]) => (
              <label key={v} style={{ display: "flex", gap: 9, alignItems: "center",
                                      padding: "6px 0", fontSize: 13.5, cursor: "pointer" }}>
                <input type="radio" name="gb-membership" value={v}
                       data-testid={`goldenbook-membership-${v}`}
                       checked={mStatus === v}
                       onChange={() => setMStatus(v)} />
                {label}
              </label>
            ))}
            {mStatus === "has_number" && (
              <input data-testid="goldenbook-membership-number" style={{ ...inputStyle, marginTop: 8 }}
                     placeholder="Membership number (e.g. MC-88214)" value={mNumber}
                     maxLength={40} onChange={(e) => setMNumber(e.target.value)} />
            )}
            {mStatus === "needs_membership" && (
              <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 8, lineHeight: 1.5 }}>
                The house will arrange your membership alongside this visit —
                one conversation, both handled.
              </div>
            )}
          </div>
        )}
        <textarea data-testid="goldenbook-arrange-note"
                  style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
                  placeholder="Anything the house should know (optional)"
                  value={note} maxLength={500}
                  onChange={(e) => setNote(e.target.value)} />
        {err && <div data-testid="goldenbook-arrange-error"
                     style={{ color: "#8E3B2F", fontSize: 13.5,
                              fontFamily: "'Inter', sans-serif" }}>{err}</div>}
        <button type="button" data-testid="goldenbook-arrange-submit"
                disabled={!ready || busy} onClick={submit}
                style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                         padding: "15px 0", fontSize: 13, fontWeight: 700,
                         letterSpacing: "0.08em", textTransform: "uppercase",
                         cursor: !ready || busy ? "not-allowed" : "pointer",
                         opacity: !ready || busy ? 0.5 : 1,
                         fontFamily: "'Inter', sans-serif" }}>
          {busy ? "Inscribing…" : "Concierge® arrange it"}
        </button>
      </div>
    </SheetShell>
  );
}

function UpsellSheet({ em, onClose, onDone }) {
  const [name, setName] = useState(em?.name || "");
  const [phone, setPhone] = useState(em?.phone || "");
  const [email, setEmail] = useState(em?.email || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ready = name.trim() && phone.trim().length >= 5;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`${API}/api/goldenbook/upsell`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(),
                               email: email.trim() || null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "failed");
      onDone(j.ref);
    } catch (_) {
      setErr("The house couldn't take that just now — please try again.");
      setBusy(false);
    }
  };

  return (
    <SheetShell testid="goldenbook-upsell-sheet" onClose={onClose}
                title="Clubs of the World"
                sub="MindEscapes Membership · the desk will call with the details">
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
        <input data-testid="goldenbook-upsell-name" style={inputStyle}
               placeholder="Your name" value={name} maxLength={120}
               onChange={(e) => setName(e.target.value)} />
        <input data-testid="goldenbook-upsell-phone" style={inputStyle}
               placeholder="WhatsApp number" value={phone} maxLength={40}
               onChange={(e) => setPhone(e.target.value)} />
        <input data-testid="goldenbook-upsell-email" style={inputStyle}
               placeholder="Email (optional)" value={email} maxLength={120}
               onChange={(e) => setEmail(e.target.value)} />
        {err && <div style={{ color: "#8E3B2F", fontSize: 13.5,
                              fontFamily: "'Inter', sans-serif" }}>{err}</div>}
        <button type="button" data-testid="goldenbook-upsell-submit"
                disabled={!ready || busy} onClick={submit}
                style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                         padding: "15px 0", fontSize: 13, fontWeight: 700,
                         letterSpacing: "0.08em", textTransform: "uppercase",
                         cursor: !ready || busy ? "not-allowed" : "pointer",
                         opacity: !ready || busy ? 0.5 : 1,
                         fontFamily: "'Inter', sans-serif" }}>
          {busy ? "Sending…" : "Have the house call me"}
        </button>
      </div>
    </SheetShell>
  );
}
